import { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader, StatusPill } from "./_shared";
import { toast } from "sonner";
import { fmtDate } from "@/lib/format";
import { Plus, UserPlus, KeyRound, Power, Building2, Download } from "lucide-react";
import { useAuth } from "@/lib/auth";

const ROLES = [
  "super_admin","brand_admin","crm_manager","marketing_manager","regional_manager",
  "store_manager","store_staff","support_agent","analytics_viewer","readonly_executive",
];

export default function UserManagement() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [show, setShow] = useState(false);
  const [form, setForm] = useState({ email: "", name: "", password: "", role: "store_manager", phone: "", region: "" });
  const [pwModal, setPwModal] = useState(null);
  const [newPw, setNewPw] = useState("");

  const load = async () => {
    const r = await api.get("/users");
    setUsers(r.data);
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    try {
      await api.post("/users", form);
      toast.success(`User ${form.email} created`);
      setShow(false);
      setForm({ email: "", name: "", password: "", role: "store_manager", phone: "", region: "" });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const toggleActive = async (u) => {
    try {
      await api.patch(`/users/${u.id}`, { is_active: !u.is_active });
      toast.success(`User ${u.is_active ? "deactivated" : "activated"}`);
      load();
    } catch (e) {
      toast.error("Failed");
    }
  };

  const resetPw = async () => {
    try {
      await api.post(`/users/${pwModal}/reset-password`, null, { params: { new_password: newPw } });
      toast.success("Password reset");
      setPwModal(null); setNewPw("");
    } catch (e) { toast.error("Failed"); }
  };

  const bulkCreateStoreLogins = async () => {
    const ok = window.confirm(
      "BULK CREATE STORE LOGINS\n\n" +
      "Creates ONE store_manager account per active store (idempotent — stores that already have a login are skipped).\n\n" +
      "Email format: store-{store_code}@orchidhotel.com\n" +
      "Password: unique random 10-char per store (shown ONCE in the response — copy & distribute).\n\n" +
      "Click OK to proceed."
    );
    if (!ok) return;
    try {
      const r = await api.post("/users/bulk-create-store-logins");
      const created = r.data?.created || [];
      const skipped = r.data?.skipped_count || 0;
      toast.success(`Created ${created.length} · Skipped ${skipped} (already exist)`, { duration: 5000 });

      // Trigger download of credentials CSV (with plain-text passwords, ONE TIME ONLY)
      if (created.length > 0) {
        const rows = [
          ["store_code","store_name","city","email","password"],
          ...created.map(c => [c.store_code, c.store_name, c.city, c.email, c.password]),
        ];
        const csv = rows.map(r => r.map(c => `"${String(c ?? "").replace(/"/g,'""')}"`).join(",")).join("\n");
        const blob = new Blob([csv], { type: "text/csv" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `orchid-store-logins-${new Date().toISOString().slice(0,10)}.csv`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
      }
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk create failed");
    }
  };

  const exportStoreLogins = async () => {
    try {
      const r = await api.get("/users/store-logins-export", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `orchid-store-logins-export-${new Date().toISOString().slice(0,10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Export failed");
    }
  };

  const availableRoles = me?.role === "super_admin" ? ROLES : ROLES.filter((r) => r !== "super_admin");

  return (
    <div data-testid="user-management-page">
      <PageHeader title="User Management" subtitle="ROLE-BASED ACCESS"
        actions={
          <div className="flex items-center gap-2">
            <button
              className="k-btn border border-emerald-700 text-emerald-800 hover:bg-emerald-50 text-[11px] uppercase tracking-widest font-semibold"
              onClick={bulkCreateStoreLogins}
              data-testid="bulk-create-store-logins-btn"
              title="Create one store_manager login per active store. Downloads CSV of credentials."
            >
              <Building2 className="w-4 h-4" /> Bulk create store logins
            </button>
            <button
              className="k-btn border border-black/30 text-neutral-800 hover:bg-black hover:text-white transition text-[11px] uppercase tracking-widest font-semibold"
              onClick={exportStoreLogins}
              data-testid="export-store-logins-btn"
              title="Download CSV of all existing store logins (WITHOUT passwords — those can only be reset)."
            >
              <Download className="w-4 h-4" /> Export store logins
            </button>
            <button className="k-btn kazo-bg-burgundy" onClick={() => setShow(true)} data-testid="new-user-btn"><UserPlus className="w-4 h-4" /> New user</button>
          </div>
        } />
      <div className="p-8">
        <div className="bg-white border border-black/10 overflow-x-auto">
          <table className="data-table">
            <thead><tr><th>Name</th><th>Email</th><th>Role</th><th>Phone</th><th>Last login</th><th>Created</th><th>Status</th><th></th></tr></thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} data-testid={`user-row-${u.id}`}>
                  <td className="font-medium">{u.name}</td>
                  <td className="font-mono text-xs">{u.email}</td>
                  <td><span className="pill pill-neutral">{u.role.replace(/_/g, " ")}</span></td>
                  <td className="text-xs">{u.phone || "—"}</td>
                  <td className="text-xs">{u.last_login_at ? fmtDate(u.last_login_at) : "—"}</td>
                  <td className="text-xs">{fmtDate(u.created_at)}</td>
                  <td><StatusPill status={u.is_active ? "active" : "inactive"} /></td>
                  <td>
                    <div className="flex gap-1">
                      <button className="k-btn k-btn-ghost k-btn-sm" title="Reset password" onClick={() => setPwModal(u.id)} data-testid={`reset-${u.id}`}><KeyRound className="w-3.5 h-3.5" /></button>
                      <button className="k-btn k-btn-ghost k-btn-sm" title={u.is_active ? "Deactivate" : "Activate"} onClick={() => toggleActive(u)} data-testid={`toggle-${u.id}`}><Power className="w-3.5 h-3.5" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {show && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShow(false)}>
          <div className="bg-white p-6 w-full max-w-lg" onClick={(e) => e.stopPropagation()} data-testid="new-user-modal">
            <h3 className="font-display text-2xl mb-4">Add User</h3>
            <div className="space-y-3">
              <input className="k-input" placeholder="Full name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="new-user-name" />
              <input className="k-input" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="new-user-email" />
              <input className="k-input" placeholder="Password" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="new-user-password" />
              <input className="k-input" placeholder="Phone (optional)" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
              <select className="k-input" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid="new-user-role">
                {availableRoles.map((r) => <option key={r} value={r}>{r.replace(/_/g, " ")}</option>)}
              </select>
              <input className="k-input" placeholder="Region (optional)" value={form.region} onChange={(e) => setForm({ ...form, region: e.target.value })} />
              <div className="flex justify-end gap-2 mt-2">
                <button className="k-btn k-btn-ghost" onClick={() => setShow(false)}>Cancel</button>
                <button className="k-btn kazo-bg-burgundy" onClick={create} data-testid="new-user-submit">Create user</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {pwModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setPwModal(null)}>
          <div className="bg-white p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-display text-2xl mb-4">Reset Password</h3>
            <input className="k-input" type="password" placeholder="New password (min 6 chars)" value={newPw} onChange={(e) => setNewPw(e.target.value)} data-testid="reset-pw-input" />
            <div className="flex justify-end gap-2 mt-4">
              <button className="k-btn k-btn-ghost" onClick={() => setPwModal(null)}>Cancel</button>
              <button className="k-btn kazo-bg-burgundy" onClick={resetPw} data-testid="reset-pw-confirm">Reset</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
